import { StyleSheet, css } from 'aphrodite';
import moment from 'moment';
import type { FC } from 'react';
import { AnimatedLionGraphic } from '../../components/svgs/AnimatedLionGraphic';
import { type CoffeeMetricsInput, type CoffeePacingBar, deriveCoffeeMetrics, type MetricTone } from './coffeeMetrics';

export type MetricsStripProps = {
	input: CoffeeMetricsInput;
	/** `top` is the compact bar row; `modal` is the roomier milestone dialog. */
	variant: 'top' | 'modal';
};

export const MetricsStrip: FC<MetricsStripProps> = ({ input, variant }) => {
	const { tiles, pacingBar } = deriveCoffeeMetrics(input);
	const isModal = variant === 'modal';

	return (
		<div className={css(styles.strip, isModal ? styles.stripModal : styles.stripTop)}>
			<div className={css(styles.tiles, isModal && styles.tilesModal)}>
				{tiles.map(tile => (
					<div key={tile.key} className={css(styles.tile)}>
						{tile.kind === 'tigers' ? (
							<div className={css(styles.tigers)}>
								{Array.from({ length: tile.demos }).map((_, i) => (
									<AnimatedLionGraphic
										key={`demo-${i}`}
										animate={false}
										className={isModal ? tigerModalClass : tigerTopClass}
									/>
								))}
							</div>
						) : (
							<div className={css(styles.value, isModal && styles.valueModal, toneClass(tile.tone))}>{tile.value}</div>
						)}
						<div className={css(styles.label, isModal && styles.labelModal)}>{tile.label}</div>
					</div>
				))}
			</div>
			{pacingBar && <PacingBar bar={pacingBar} isModal={isModal} />}
		</div>
	);
};

const PacingBar: FC<{ bar: CoffeePacingBar; isModal: boolean }> = ({ bar, isModal }) => {
	const paceLabel = `You should be here by ${moment().format('h:mma')}`;
	return (
		<div className={css(styles.pacing, isModal && styles.pacingModal)}>
			<div className={css(styles.pacingHead)}>
				<span className={css(styles.pacingValue)}>
					{bar.current} / {bar.goal}
				</span>
				<span className={css(styles.pacingLabel)}>{bar.label}</span>
			</div>
			<div
				className={css(styles.track)}
				role='progressbar'
				aria-label='Daily pacing'
				aria-valuenow={bar.current}
				aria-valuemin={0}
				aria-valuemax={bar.goal}
			>
				<div className={css(styles.fill, toneClass(bar.tone))} style={{ width: `${bar.fillPct}%` }} />
				<div className={css(styles.marker)} style={{ left: `${bar.markerPct}%` }} title={paceLabel} />
			</div>
			{isModal && <div className={css(styles.markerText)}>{paceLabel}</div>}
		</div>
	);
};

function toneClass(tone: MetricTone) {
	return tone === 'green' ? styles.toneGreen : tone === 'yellow' ? styles.toneYellow : styles.toneRed;
}

const styles = StyleSheet.create({
	strip: {
		display: 'flex',
		alignItems: 'center',
		minWidth: 0,
	},
	stripTop: {
		gap: 14,
		width: '100%',
		overflowX: 'auto',
	},
	stripModal: {
		flexDirection: 'column',
		gap: 16,
		width: '100%',
	},
	tiles: {
		display: 'flex',
		alignItems: 'center',
		gap: 14,
		minWidth: 0,
	},
	tilesModal: {
		width: '100%',
		justifyContent: 'space-around',
		gap: 8,
	},
	tile: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 2,
		whiteSpace: 'nowrap',
	},
	value: {
		fontSize: 16,
		fontWeight: 700,
		lineHeight: 1,
		color: 'var(--ink)',
	},
	valueModal: {
		fontSize: 22,
	},
	label: {
		fontSize: 10,
		fontWeight: 500,
		color: 'var(--ink-muted)',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	labelModal: {
		fontSize: 11,
	},
	toneGreen: { color: 'var(--pace-green)' },
	toneYellow: { color: 'var(--pace-yellow)' },
	toneRed: { color: 'var(--pace-red)' },
	tigers: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		height: 22,
	},
	pacing: {
		display: 'flex',
		flexDirection: 'column',
		gap: 3,
		minWidth: 140,
		flex: 1,
	},
	pacingModal: {
		width: '100%',
	},
	pacingHead: {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 8,
		fontSize: 11,
		fontWeight: 600,
		color: 'var(--ink-muted)',
	},
	pacingValue: {
		color: 'var(--ink)',
	},
	pacingLabel: {
		letterSpacing: '0.06em',
	},
	track: {
		position: 'relative',
		height: 6,
		borderRadius: 3,
		background: 'var(--bg-sunken)',
		overflow: 'hidden',
	},
	fill: {
		position: 'absolute',
		left: 0,
		top: 0,
		bottom: 0,
		borderRadius: 3,
		transition: 'width 200ms ease-out',
	},
	marker: {
		position: 'absolute',
		top: -2,
		bottom: -2,
		width: 2,
		background: 'var(--ink)',
		opacity: 0.5,
	},
	markerText: {
		fontSize: 11,
		color: 'var(--ink-faint)',
	},
});

// AnimatedLionGraphic scales via CSS width/height (its SvgIcon keeps a viewBox),
// matching the legacy 25px banner tiger; the modal shows them a touch larger.
const tigerStyles = StyleSheet.create({
	top: { width: 20, height: 20 },
	modal: { width: 30, height: 30 },
});
const tigerTopClass = css(tigerStyles.top);
const tigerModalClass = css(tigerStyles.modal);
